'use client';

import EmptyChatMessageInput from '@/components/SearchInput/EmptyChatMessageInput';
import { useCreateChat } from '@/hooks/useCreateChat';
import { useCallback, useMemo, useState } from 'react';
import ChatMain from '../ui/ChatMain';
import { createPortal } from 'react-dom';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEventEmitter } from '@/hooks/useEventEmitter';
import { useFixedChatStore } from '@/stores/fixedChatStore';
import { useUnmount } from 'ahooks';
import { useSidebar } from '@/contexts/SidebarContext';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from '@/lib/i18n';
import { useUserContextStore } from '@/stores/userContextStore';
import { useMarketDrawerStore } from '@/stores/marketDrawerStore';
import Drawer from '@mui/material/Drawer';
import { useChats } from '@/contexts/ChatContext';

interface FixedChatWindowProps {
  suggestions: string[];
  userContext?: string;
  className?: string;
  sourceId?: string;
  queryType?: 'auto' | 'crypto' | 'predx';
}
export default function FixedChatWindow({
  suggestions,
  className,
  userContext,
  sourceId,
  queryType = 'auto',
}: FixedChatWindowProps) {
  const { t } = useTranslation();
  const { createChat, isCreating } = useCreateChat();
  const { refreshChats } = useChats();
  const [fullscreen, setFullscreen] = useState(true);
  const { chatId, setChatId } = useFixedChatStore();
  const [currentQuery, setCurrentQuery] = useState<string>();
  const emitter = useEventEmitter();
  const { isCollapsed } = useSidebar();
  const { isOpen: isRightDrawerOpen } = useRightDrawer();
  const { isOpen: isNotificationOpen } = useNotificationStore();
  const setUserContextFromString = useUserContextStore((state) => state.setUserContextFromString);
  const isSidePanelOpen = isRightDrawerOpen || isNotificationOpen;

  const isMarketDrawerOpen = useMarketDrawerStore((state) => state.isOpen);
  const marketDrawerContent = useMarketDrawerStore((state) => state.content);
  const marketDrawerWidth = useMarketDrawerStore((state) => state.width);
  const closeMarketDrawer = useMarketDrawerStore((state) => state.closeMarketDrawer);

  const inputStyle = useMemo(() => {
    const sidebarWidth = isCollapsed ? 60 : 260;
    const rightPanelWidth = isSidePanelOpen ? 400 : 0;

    return {
      marginLeft: isCollapsed
        ? isSidePanelOpen
          ? '-170px'
          : '30px'
        : isSidePanelOpen
          ? '-70px'
          : '130px',
      width: `min(760px, calc(100vw - ${sidebarWidth + rightPanelWidth + 50}px))`,
    };
  }, [isCollapsed, isSidePanelOpen]);

  const onChatCreateSuccess = useCallback(
    ({ chatId, currentQuery }: { chatId: string; currentQuery?: string }) => {
      if (userContext) {
        setUserContextFromString(userContext);
      }
      refreshChats();
      setChatId(chatId);
      setCurrentQuery(currentQuery);
    },
    [setChatId, setUserContextFromString, userContext, refreshChats],
  );

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (chatId) {
        emitter.emit('air-sendmessage', message);
      } else {
        createChat({ message, onSuccess: onChatCreateSuccess, queryType, source: sourceId });
      }
    },
    [chatId, createChat, emitter, onChatCreateSuccess, queryType, sourceId],
  );

  // 关闭浮层时一并清掉抽屉内容，避免下次打开时残留上一次的市场详情
  const closeChatWindow = useCallback(() => {
    closeMarketDrawer();
    setChatId(undefined);
  }, [closeMarketDrawer, setChatId]);

  useUnmount(() => {
    closeMarketDrawer();
    setChatId(undefined);
  });

  return (
    <>
      {chatId &&
        createPortal(
          <>
            <div
              className={cn('fixed top-0 right-0 bottom-0 bg-bg-main z-50 overflow-scroll', {
                'left-65': fullscreen && !isCollapsed,
                'left-15': fullscreen && isCollapsed,
                'w-100 border-l border-white/15': !fullscreen,
              })}
            >
              <header className="h-15 p-4 flex items-center justify-between gap-3 sticky top-0 bg-bg-main z-10">
                <button
                  className="rounded-md size-6 flex items-center justify-center hover:bg-[#171717]"
                  onClick={closeChatWindow}
                >
                  <X size={20} />
                </button>
                <button
                  className="rounded-md size-6 flex items-center justify-center bg-white text-black"
                  onClick={() => setFullscreen((f) => !f)}
                >
                  {fullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
              </header>
              <div className="px-4">
                <ChatMain
                  chatId={chatId}
                  floating={!fullscreen}
                  currentQuery={currentQuery}
                  onCurrentQueryConsumed={() => setCurrentQuery(undefined)}
                />
              </div>
            </div>

            {/* 浮层内部的右侧抽屉：全局 RightDrawer 在根布局里会被上面的 z-50 浮层盖住，
              所以非会话页的市场卡片改用这个抽屉展示（内容来自 marketDrawerStore） */}
            <Drawer
              anchor="right"
              open={isMarketDrawerOpen}
              onClose={closeMarketDrawer}
              sx={{ zIndex: 60 }}
              slotProps={{
                paper: {
                  sx: {
                    width: marketDrawerWidth,
                    maxWidth: '100vw',
                    bgcolor: '#000',
                    color: '#fff',
                  },
                },
              }}
            >
              <div className="min-h-full h-full overflow-auto border-l border-white/15">
                <header className="h-15 px-4 flex items-center sticky top-0 bg-black z-10">
                  <button
                    className="rounded-md size-6 flex items-center justify-center hover:bg-[#171717]"
                    onClick={closeMarketDrawer}
                    aria-label={t('Close')}
                  >
                    <X size={20} />
                  </button>
                </header>
                <div className="px-4 pb-8">{marketDrawerContent}</div>
              </div>
            </Drawer>
          </>,
          document.body,
        )}

      <div
        className={cn('fixed bottom-0 left-1/2 z-50 -translate-x-1/2 py-4', className)}
        style={inputStyle}
      >
        <EmptyChatMessageInput
          loading={isCreating}
          sendMessage={handleSendMessage}
          isSmall={false}
          floating={true}
          suggestions={chatId ? [] : suggestions || []}
        />
      </div>
    </>
  );
}
